import styled, { css } from 'styled-components'
import { IconRegularClose as NotinoCloseIcon, theme } from '@notino/react-styleguide'

export const ChipRoot = styled.div<{ $isSelected?: boolean; $isMainButtonChip?: boolean; $isDisabled?: boolean }>`
	${theme.typography.labelSmall400};
	white-space: nowrap;
	border: 1px solid transparent;
	border-radius: 999px;
	background-color: ${theme.color.background.secondary};
	padding: 0.5rem 1rem;
	display: inline-flex;
	align-items: center;
	height: 2rem;
	transition:
		border-color 0.3s ease,
		color 0.3s ease;
	gap: 0;
	position: relative;
	color: ${theme.color.text.primary};

	${({ $isSelected, $isMainButtonChip }) =>
		$isSelected &&
		!$isMainButtonChip &&
		css`
			${theme.typography.labelSmall};
			border-color: ${theme.color.border.divider};
		`}

	${({ $isDisabled }) =>
		$isDisabled &&
		css`
			color: ${theme.color.text.disabled};
		`}
`

export const ChipAction = styled.button<{ $hasClose?: boolean }>`
	appearance: none;
	border: none;
	background: transparent;
	padding: 0;
	margin: 0;
	display: inline-flex;
	align-items: center;
	/* Fill the whole chip hit area; close sits visually on top */
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	padding: 0.5rem 1rem;
	box-sizing: border-box;
	font: inherit;
	font-weight: inherit;
	color: inherit;
	cursor: pointer;
	border-radius: 999px;
	outline: 2px solid transparent;
	z-index: 0;

	${({ $hasClose }) =>
		$hasClose &&
		css`
			/* Reserve space for the 0.75rem icon + 0.625rem gap (same as previous inline layout) */
			padding-right: 2.375rem;
		`}

	&:focus-visible {
		outline-color: ${theme.color.border.selected};
	}

	&:disabled {
		cursor: default;
		color: ${theme.color.text.disabled};
	}
`

export const Label = styled.span`
	display: inline-block;
`

export const SelectedBadge = styled.span<{ $isSelected?: boolean; $disabled?: boolean }>`
	position: absolute;
	display: block;
	border-radius: 50%;
	width: 0.375rem;
	height: 0.375rem;
	top: 0.4375rem;
	right: 0.8125rem;
	background-color: ${theme.color.background.highlight};
	pointer-events: none;
	opacity: 0;
	transition:
		opacity 0.3s ease,
		background-color 0.3s ease;

	${({ $isSelected }) =>
		$isSelected &&
		css`
			opacity: 1;
		`}

	${({ $disabled }) =>
		$disabled &&
		css`
			background-color: #f06292;
		`}
`

export const Count = styled.span<{ $disabled?: boolean }>`
	display: inline-block;
	color: ${theme.color.text.tertiary};
	font-weight: 500;
	margin-left: 0.25rem;

	${({ $disabled }) =>
		$disabled
			? css`
					color: ${theme.color.text.disabled};
				`
			: css`
					// NOTE: farba z novej palety
					color: ${theme.color.text.tertiary};
				`}
`

export const CloseButton = styled.button<{ $disabled?: boolean }>`
	appearance: none;
	border: none;
	background: transparent;
	padding: 0;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 0.75rem;
	height: 0.75rem;
	position: absolute;
	right: 1rem;
	top: 50%;
	transform: translateY(-50%);
	z-index: 1;
	transition: all 0.3s ease;
	overflow: hidden;
	outline: 2px solid transparent;
	outline-offset: 2px;

	&:focus-visible {
		outline-color: ${theme.color.border.selected};
	}

	${({ $disabled }) =>
		$disabled
			? css`
					color: ${theme.color.text.disabled};
					cursor: default;
				`
			: css`
					// NOTE: farba z novej palety
					color: ${theme.color.text.tertiary};
					cursor: pointer;
				`}
`

/** Non-interactive X for toggle chips — clicks fall through to ChipAction */
export const CloseVisual = styled.span<{ $disabled?: boolean }>`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 0.75rem;
	height: 0.75rem;
	position: absolute;
	right: 1rem;
	top: 50%;
	transform: translateY(-50%);
	z-index: 1;
	pointer-events: none;
	overflow: hidden;

	${({ $disabled }) =>
		$disabled
			? css`
					color: ${theme.color.text.disabled};
				`
			: css`
					// NOTE: farba z novej palety
					color: ${theme.color.text.tertiary};
				`}
`

export const CloseIcon = styled(NotinoCloseIcon)<{ $isSelected?: boolean }>`
	color: inherit;
	width: 100%;
	height: 100%;
	display: block;
`

/**
 * In-flow width spacer: both action and close are absolutely positioned,
 * so this mirrors the visible content so the pill keeps the correct width.
 */
export const ChipWidthSizer = styled.span<{ $hasClose?: boolean }>`
	visibility: hidden;
	pointer-events: none;
	display: inline-flex;
	align-items: center;
	white-space: nowrap;

	${({ $hasClose }) =>
		$hasClose &&
		css`
			/* Same reserved space as ChipAction padding-right when close is shown (0.625rem gap + 0.75rem icon) */
			padding-right: 1.375rem;
		`}
`
